import { useApplicationList } from "@/hooks/application-list.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { buildQuizByIdQueryKey } from "@/hooks/quiz-by-id.hook";
import {
  quizById,
  QuizByIdResponse,
} from "@/requests/questionnaries/quiz-by-id.request";
import { Application } from "@/types/applications";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { Book1, ClipboardText, Graph, Home2, Timer } from "iconsax-react";
import { Dispatch, FC, useEffect, useState } from "react";
import NavigationItem, { NavigationItemProps } from "./navigation-item";

const preloadQuizzes = async (
  applications: Application[],
  companyId: string,
  setQuizzes: Dispatch<QuizByIdResponse[]>,
  queryClient: QueryClient,
) => {
  const items = await Promise.all(
    applications.map(async (application) => {
      const params = {
        companyId: companyId,
        applicationQuizId: application.quizId || "",
      };
      return await queryClient.fetchQuery<QuizByIdResponse>({
        queryKey: buildQuizByIdQueryKey(params),
        queryFn: () => quizById(params),
      });
    }),
  );

  setQuizzes(items);
};

const userCanViewResults = (quizId: string, quizzes: QuizByIdResponse[]) => {
  const quiz = quizzes.find((quiz) => quiz.id === quizId);
  return (
    Boolean(quiz?.status === "DELIVERED" && quiz?.result) ||
    (quiz?.appealReviewCount || 0) > 0
  );
};

const Sidebar: FC = () => {
  const { applications, isLoading: isApplicationsLoading } =
    useApplicationList();

  const queryClient = useQueryClient();

  const { data: userProfile } = useCurrentUserProfile();

  const usableApplications = applications.filter((application) =>
    Boolean(application.quizId),
  );

  const [quizzes, setQuizzes] = useState<QuizByIdResponse[]>([]);

  const quizNavChildItems = usableApplications.map((application) => ({
    label: application.edition.name,
    href: `/editions/${application.edition.id}/quiz`,
    disabled: !application.quizId,
  }));

  const reportNavChildItems = usableApplications.map((application) => ({
    label: application.edition.name,
    href: `/editions/${application.edition.id}/quizzes/${application.quizId}/results`,
    disabled: !userCanViewResults(application.quizId || "", quizzes),
    disabledReason: "No tienes permiso para ver los resultados.",
  }));

  const menuItems: NavigationItemProps[] = [
    {
      label: "Inicio",
      href: "/",
      icon: <Home2 size={20} />,
    },
    {
      label: "Cuestionario",
      href: quizNavChildItems[0]?.href || "/",
      icon: <ClipboardText size={20} />,
      disabled:
        quizNavChildItems.length === 0 ||
        quizNavChildItems.some((item) => item.disabled),
      disabledReason: "No hay cuestionarios disponibles.",
      navChildren: quizNavChildItems,
    },
    {
      label: "Biblioteca de recursos",
      href: "/library",
      icon: <Book1 size={20} />,
    },
    {
      label: "Reporte de resultados",
      href: reportNavChildItems[0]?.href || "/",
      icon: <Graph size={20} />,
      disabled:
        reportNavChildItems.length === 0 ||
        reportNavChildItems.some((item) => item.disabled),
      disabledReason: "Aún no hay resultados disponibles.",
      navChildren: reportNavChildItems,
    },
    {
      label: "Ediciones anteriores",
      href: "/previous-editions",
      icon: <Timer size={20} />,
    },
  ];

  // Preload quizzes to check if the user can view the results
  useEffect(() => {
    if (userProfile?.companyId) {
      preloadQuizzes(
        usableApplications,
        userProfile.companyId,
        setQuizzes,
        queryClient,
      );
    }
  }, [isApplicationsLoading]);

  return (
    <aside className="[grid-area:sidebar]">
      <nav>
        <ul className="m-2 mt-6 flex flex-col gap-3">
          {menuItems.map((item) => (
            <li key={item.label}>
              <NavigationItem {...item} />
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
